import User from '~/model/user';
import { Store } from 'react-notifications-component';
import config from "~/config";
import UserRole from '~/model/userRole';
import DOMPurify from 'dompurify';
import regx from '~/constants/regx';
import { LANGUAGE_NONE } from '~/constants/language';
import { UncontrolledTooltip } from "reactstrap";
import Order from "~/model/order";
import type { TListParams } from "~/types/global";

export const sleep = (time: number) => new Promise((resolve) => setTimeout(resolve, time));

export const getSharedPaginationOptions = () => ({
  custom: true,
  hidePageListOnlyOnePage: true,
  paginationSize: 10,
  sizePerPageList: [
    { text: '1', value: 1 },
    { text: '50', value: 50 },
    { text: '100', value: 100 },
    { text: '200', value: 200 },
  ],
});

export const getSharedTableOptions = () => ({
  keyField: "id",
  bordered: false,
  striped: false,
  defaultSortDirection: "asc",
  selectRow: {
    mode: 'radio',
    hideSelectColumn: true,
  },
  classes: "table align-middle table-nowrap",
  headerWrapperClasses: "thead-light",
  responsive: true,
  remote: true,
});

export const valueIsDefined = (value: any): boolean => value !== undefined && value !== null;

export const valueIsEmpty = (value: any): boolean => {
  let val = value;
  if (typeof val === 'string') {
    val = val.trim();
  }

  if (Array.isArray(val) && val.length === 0) {
    return true;
  }

  // allow 0 and FALSE
  return val !== 0 && val !== false && !val;
}

export const mapNonEmpty = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const result: Partial<T> = {};
  return Object.entries(obj).reduce((a, [k, v]) => {
    if (!valueIsEmpty(v)) {
      (a as any)[k] = v;
    }
    return a;
  }, result);
}

export const hasNonEmpty = (obj: Record<string, any>): boolean =>
  Object.keys(mapNonEmpty(obj)).length > 0;

// axios serializes only plain objects
// so any nested objects will end up as json
// but we need the filters as a query string array
// so here we do the transformation
export const flattenFilters = (params: TListParams): Record<string, any> => {
  // clone the object to avoid changing the original
  const newParams: Record<string, any> = { ...params };
  if (newParams.filters) {
    // loop through all filters
    for (const [key, value] of Object.entries(newParams.filters)) {
      // if the filter value is not empty
      if (!valueIsEmpty(value)) {
        // add the filter under the root object
        // change the key so it will be decoded properly on the backend
        newParams[`filters[${key}]`] = value;
      }
    }
    // remove the filters key since it is no longer needed
    delete newParams.filters;
  }
  return newParams;
}

// the Select control expects a list of {value, label} objects as options
// most of the time api response is different than what the Select control expects
// so here we do the transformation
export interface SelectOption {
  label: string;
  value: string | number;
}

export const toSelectOptions = <T extends Record<string, any>>(
  list: T[],
  valueProp: keyof T = 'id' as keyof T,
  labelProp: keyof T = 'name' as keyof T
): SelectOption[] => {
  return list.map((item: T) => ({
    label: String(item[labelProp]),
    value: item[valueProp],
  }));
}

export const getYesNoOptions = (includeAll: boolean = false): SelectOption[] => {
  const options: SelectOption[] = [{
    label: "Yes",
    value: 1,
  }, {
    label: "No",
    value: 0
  }];
  if (includeAll) {
    options.unshift({
      label: "All",
      value: '',
    });
  }
  return options;
}

export const distanceServedOptions: SelectOption[] = [
  {
    value: 10,
    label: "10 miles radius"
  },
  {
    value: 20,
    label: "20 miles radius"
  },
  {
    value: 30,
    label: "30 miles radius"
  },
  {
    value: 40,
    label: "40 miles radius"
  },
  {
    value: 50,
    label: "50 miles radius"
  },
];

export const isObject = (obj: any): obj is Record<string, any> => typeof obj === 'object' && obj !== null;

export const showNotification = (
  type: string,
  message: string | React.ReactNode,
  options?: any
): string => {
  const defaultOptions = {
    title: '',
    insert: 'top',
    container: 'top-center',
    animationIn: ['animate__animated', 'animate__fadeIn'],
    animationOut: ['animate__animated', 'animate__fadeOut'],
    dismiss: {
      duration: 6000,
      onScreen: true,
      pauseOnHover: true,
      showIcon: true,
    },
    width: 400,
  }
  return Store.addNotification({
    ...defaultOptions,
    ...options,
    type: type,
    message: message,
  });
}

export const showBriefNotification = (
  type: string,
  message: string | React.ReactNode,
  options?: any
): string => {
  const defaultOptions = {
    title: '',
    insert: 'top',
    container: 'top-center',
    animationIn: ['animate__animated', 'animate__fadeIn'],
    animationOut: ['animate__animated', 'animate__fadeOut'],
    dismiss: {
      duration: 2000,
      onScreen: true,
      pauseOnHover: true,
      showIcon: true,
    },
    width: 400,
  }
  return Store.addNotification({
    ...defaultOptions,
    ...options,
    type: type,
    message: message,
  });
}

export const showSuccess = (message: string | React.ReactNode, options?: any): string =>
  showNotification('success', message, options);

export const showBriefSuccess = (message: string | React.ReactNode, options?: any): string =>
  showBriefNotification('success', message, options);

export const showMessage = (message: string | React.ReactNode, options?: any): string =>
  showNotification('info', message, options);

export const showWarning = (message: string | React.ReactNode, options?: any): string =>
  showNotification('warning', message, options);

export const showError = (message: string | React.ReactNode, options?: any): string =>
  showNotification('danger', message, options);

export const showBriefError = (message: string | React.ReactNode, options?: any): string =>
  showBriefNotification('danger', message, options);

export const removeNotification = (id: string) => Store.removeNotification(id);

export const trim = (str: string, char: string): string => {
  const escapedChar = char.replace(/[-/^$*+?.()|[]{}]/g, '$&');
  return str.replace(new RegExp(
    "^[" + escapedChar + "]+|[" + escapedChar + "]+$", "g"
  ), "");
}

export const ltrim = (s: string, c: string): string => {
  const escapedC = c.replace(/[-/$*+?.()|[]{}]/g, '&');
  return s.replace(new RegExp(
    "^[" + escapedC + "]+", "g"
  ), "");
}

export const rtrim = (s: string, c: string): string => {
  const escapedC = c.replace(/[-/$*+?.()|[]{}]/g, '&');
  return s.replace(new RegExp(
    "[" + escapedC + "]+$", "g"
  ), "");
}

export const buildUrl = (base: string, fragment?: string | null): string =>
  rtrim(base, '/') + (fragment ? '/' + ltrim(fragment, '/') : '');

export const getBeUrl = (fragment?: string | null): string =>
  buildUrl((config as any).API_BE_URL, fragment);

export const getSsoAppUrl = (fragment?: string | null): string =>
  buildUrl((config as any).SSO_URL, fragment);

export const getAdminAppUrl = (fragment?: string | null): string =>
  buildUrl((config as any).ADMIN_URL, fragment);

export const getDealerAppUrl = (fragment?: string | null): string =>
  buildUrl((config as any).DEALER_URL, fragment);

export const nullsToEmptyStrings = <T,>(obj: T): T | undefined => {
  if (!obj) {
    return undefined;
  }
  if (Array.isArray(obj)) {
    return obj.map(v => v === null ? '' : v) as T;
  } else {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === null ? '' : v])) as T;
  }
}

export const toLanguageCodes = (value: unknown): string[] => Array.isArray(value) ? value as string[] : [];

// an empty array means 'no additional languages',
// the form holds LANGUAGE_NONE for the first case, so that it still passes the mandatory check
export const toLanguageFormValue = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.length ? value as string[] : [LANGUAGE_NONE];
}

export const fromLanguageFormValue = (codes: string[]): string[] =>
  codes.includes(LANGUAGE_NONE) ? [] : codes;

export const isNotAllOrNone = (value: any): boolean => value != '' && value != -1;

export const toUserModel = (user: any): User => Object.assign(new User(), user);

const US_TIMEZONES = [
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Chicago',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'Pacific/Honolulu',
];

const getTimezoneAbbrIntl = (zone: string) =>
  new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(new Date())
    .find(part => part.type === 'timeZoneName')?.value ?? zone;

export const getTimezoneOptions = () => US_TIMEZONES.map(zone => ({
  label: `${zone} (${getTimezoneAbbrIntl(zone)})`,
  value: zone,
}));

export const getTimezoneAbbr = (zone: string) => getTimezoneAbbrIntl(zone);

export interface UserOwner {
  name: string | null;
  id: number | null;
  route: string | null;
  type: string | null;
  parentName?: string;
  parentId?: number;
  parentRoute?: string;
}

export const getUserOwner = (user: any): UserOwner => {
  const owner: UserOwner = {
    name: null,
    id: null,
    route: null,
    type: null,
  };
  if (UserRole.isAdminType(user.userRoleId)) {
    owner.name = (config as any).APP_TITLE;
    owner.type = 'mav';
  } else if (UserRole.isDealerGroupManager(user.userRoleId)) {
    owner.name = user.dealerGroupName;
    owner.id = user.dealerGroupId;
    owner.route = 'view_dealer_group';
    owner.type = 'dealerGroup';
  } else if (UserRole.isDealerRegionalManager(user.userRoleId)) {
    owner.name = user.dealerRegionName;
    owner.id = user.dealerRegionId;
    owner.route = 'view_dealer_region';
    owner.type = 'dealerRegion';
    owner.parentName = user.dealerGroupName;
    owner.parentId = user.dealerGroupId;
    owner.parentRoute = 'view_dealer_group';
  } else if (UserRole.isDealerType(user.userRoleId)) {
    owner.name = user.dealerStoreName;
    owner.id = user.dealerStoreId;
    owner.route = 'view_dealer_store';
    owner.type = 'dealerStore';
  }
  return owner;
}

export const withAuth = (fragment?: string | null): string =>
  buildUrl((config as any).AUTH_PATH_PREFIX, fragment);

export const randomString = (len: number): Promise<string> => new Promise((resolve, reject) => {
  try {
    const array = new Uint8Array(len);
    window.crypto.getRandomValues(array);
    const hex = Array.from(array)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    resolve(hex);
  } catch (error) {
    reject(null);
  }
});

export const randomStringSync = (len: number): string => {
  const array = new Uint8Array(len);
  window.crypto.getRandomValues(array);

  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

export const mergeRefs = <T = any>(...refs: Array<React.Ref<T> | null | undefined>): ((inst: T | null) => void) | null => {
  const filteredRefs = refs.filter(Boolean) as Array<React.Ref<T>>;
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 1) return (inst: T | null) => {
    const ref = filteredRefs[0];
    if (typeof ref === 'function') {
      ref(inst);
    } else if (ref && 'current' in ref) {
      (ref as React.MutableRefObject<T | null>).current = inst;
    }
  };
  return (inst: T | null) => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(inst);
      } else if (ref && 'current' in ref) {
        (ref as React.MutableRefObject<T | null>).current = inst;
      }
    }
  };
};

export const dictionaryToSelectOptions = (dict: Record<string, string>): SelectOption[] =>
  Object.entries(dict).map(entry => ({ label: entry[1], value: entry[0] }));

export const formatPhone = (phone: string | null | undefined): string | undefined => {
  if (!phone) {
    return undefined;
  }
  const clean = phone.replace(/\D/g, '');
  const match = clean.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!match) {
    return clean;
  }
  let result = '';
  if (!!match[1]) {
    result += match[1];
  }
  if (!!match[2]) {
    result += '-' + match[2];
  }
  if (!!match[3]) {
    result += '-' + match[3];
  }
  return result;
}

export const openInNewWindow = (url: string) => {
  // Safari is blocking any call to open a new window which is made inside an async call, treating it as a pop-up.
  // setTimeout code runs on the main thread, instead of the asynchronous one
  setTimeout(() => {
    const a = document.createElement('a');
    a.target = '_blank';
    a.href = url;
    a.click();
  })
}

export const sanitizeHtml = (html: string): string => DOMPurify.sanitize(html);

export const stripTags = (value: string | null | undefined): string | undefined =>
  value?.replace(/(<([^>]+)>)/gi, "");

export const getInitialsFromName = (name: string): string => {
  const match = name && name.match(/\b(\w)/g);
  return match ? match.join('') : '';
}

export const filterObjectKeys = <T extends Record<string, any>>(
  obj: T | null,
  keys: (keyof T)[]
): Partial<T> | null => {
  if (!obj) {
    return null;
  }
  return Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key as keyof T))) as Partial<T>;
}

export const extractDtFiltersFromUrl = (): Record<string, string> => {
  const query = new URLSearchParams(window.location.search);
  const filters: Record<string, string> = {};
  for (const [key, value] of query.entries()) {
    const match = key.match(/filters\[(.+)\]/);
    if (!!match && !!match[1]) {
      filters[match[1]] = value;
    }
  }
  return filters;
}

export const removeDtFiltersFromUrl = (): string => {
  const query = new URLSearchParams(window.location.search);
  const filters: Record<string, string> = {};
  for (const [key, value] of query.entries()) {
    const match = key.match(/filters\[(.+)\]/);
    if (!match) {
      filters[key] = value;
    }
  }
  return new URLSearchParams(filters).toString();
}

export const getMiles = (meters: number): string => (meters * 0.000621371192).toFixed(2);

export const getMeters = (miles: number): number => (miles * 1609.344);

export interface AddressComponents {
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface GooglePlaceComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface GooglePlace {
  address_components: GooglePlaceComponent[];
}

export const getAddressComponents = (place: GooglePlace | null | undefined): AddressComponents => {
  const addressComponents: AddressComponents = {
    address: "",
    city: "",
    state: "",
    zip: "",
  };

  if (!place?.address_components || !Array.isArray(place.address_components)) {
    return addressComponents;
  }

  // get each component of the address from the place details,
  // and then fill-in the corresponding field on the form.
  // place.address_components are google.maps.GeocoderAddressComponent objects
  // which are documented at http://goo.gle/3l5i5Mr
  for (const component of place.address_components) {
    const types = component.types;

    if (types.includes("street_number")) {
      addressComponents.address = `${component.long_name} ${addressComponents.address}`;
    }
    if (types.includes("route")) {
      addressComponents.address += component.short_name;
    }
    if (types.includes("postal_code")) {
      addressComponents.zip = component.long_name;
    }
    if (types.includes("locality")) {
      addressComponents.city = component.long_name;
    }
    if (types.includes("administrative_area_level_1")) {
      addressComponents.state = component.short_name;
    }
  }

  return addressComponents;
}

export const toBase64 = (file: File | Blob): Promise<string | ArrayBuffer | null> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = error => reject(error);
});

export const bytesToSize = (bytes: number): string => {
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) return 'n/a'
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString(), 10)
  if (i === 0) return `${bytes} ${sizes[i]}`
  return `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`
}

export const decimalToDMS = (decimal?: number, isLatitude?: boolean): string => {
  if (decimal === undefined || decimal === null) {
    return '';
  }

  const degrees = Math.floor(Math.abs(decimal));
  const decimalMinutes = (Math.abs(decimal) - degrees) * 60;
  const minutes = Math.floor(decimalMinutes);
  const seconds = (decimalMinutes - minutes) * 60;

  let direction = "";
  if (isLatitude) {
    direction = decimal >= 0 ? 'N' : 'S';
  } else {
    direction = decimal >= 0 ? 'E' : 'W';
  }

  return `${degrees}° ${minutes}' ${seconds.toFixed(2)}" ${direction}`;
}

export const newLineTextFormat = (lines: string[] | null | undefined): string[] | undefined => {
  // Add each line of text on new line
  // and add bullet symbol at the beginning
  if (!lines) return undefined;
  return lines.map(function (line) {
    return '\u2022' + ' ' + line;
  });
}

export const phoneHasNoOfDigits = (value: string | null | undefined): boolean => {
  return !!value && value.replace((regx as any).phoneChars, '').length == (config as any).PHONE_NR_OF_DIGITS;
}

export interface PaginationProps {
  page: number;
  sizePerPage: number;
}

export const getDtFooterRowCount = (
  paginationProps: PaginationProps,
  numOfVisibleRows: number,
  totalNumOfRows: number
): React.ReactNode => {
  const startNum = ((paginationProps.page - 1) * paginationProps.sizePerPage) + 1;
  const endNum = ((paginationProps.page - 1) * paginationProps.sizePerPage) + numOfVisibleRows;
  return <>
    Showing <span id="dt-rowcount">{numOfVisibleRows}</span> of {totalNumOfRows} items
    <UncontrolledTooltip placement="top" target="dt-rowcount">{startNum}-{endNum}</UncontrolledTooltip>
  </>;
}

export const getAoiUrl = (isThumbprintRequired: boolean): string => {
  if (isThumbprintRequired) return getBeUrl(`notary/std-doc/aoi.pdf`);

  return getBeUrl(`notary/std-doc/aoi_no_fingerprint.pdf`);
}

// regex to check if the massage has links
const urlRegex = /((https?:\/\/|www\.)[^\s]+)/gi;

// Function that formats a message by URLs
export const formatMessage = (value: string | null | undefined): string => {
  if (!value) return "";

  // Replace URLs
  const urlReplacement = (match: string): string => {
    const href = match.startsWith('www.') ? `https://${match}` : match;
    return `<a href="${href}" target="_blank" style="color: #3b82f6;">${match}</a>`;
  };

  // Step-by-step replacements
  let formatted = value;

  // Replace URLs
  formatted = formatted.replace(urlRegex, urlReplacement);

  return formatted;
};

export const getDealerInstructionsText = (
  dealerInstructions: string | null | undefined,
  caDeliveryForm: number | null | undefined,
  noInstructionsText: string
): string => {
  if (!dealerInstructions && !caDeliveryForm) return noInstructionsText;

  if (caDeliveryForm && [Order.CA_DELIVERY_FORM_YES_BOTH_CONTRACTS, Order.CA_DELIVERY_FORM_YES].includes(caDeliveryForm)) {
    const specialInstructions = Order.getCalifornia448SpecialInstruction();
    const instruction = specialInstructions[caDeliveryForm as keyof typeof specialInstructions];
    return `${!!dealerInstructions ? dealerInstructions : ""} ${instruction || ""}`
  }
  return dealerInstructions || "";
}